/* eslint-disable import/no-unresolved */

import { ParsedUrlQuery } from 'querystring';

import { useEffect, useMemo, useRef, useState } from 'react';

import { SwiperSlide } from 'swiper/react';
import SwiperCore from 'swiper';
import { useSearchParams } from 'next/navigation';

import { ChevronLeft, ChevronRight } from '@assets/icons';
import CustomIcon from '@components/utils/CustomIcon';
import { useDevice } from '@contexts/device';
import { PackageTransferViewModel } from '@api/v3';
import { FlightProps } from '@components/views/package/components/helper';
import { paramsToObject } from '@components/views/utils';

import { RoomsVariationBarItem as ItemProps } from './Item/props';
import { StyledSwiper, Wrapper, PreviousElement, NextElement } from './styled';
import RoomsVariationBarItem from './Item';

import 'swiper/css';
import 'swiper/css/navigation';

const RoomsVariationsBar = ({
  items,
  activeItem,
  toggleActiveItem,
  selectedDepatureFlight,
  selectedDepatureTransfer,
  selectedReturnFlight,
  selectedReturnTransfer,
  adultCount,
  childCount,
}: {
  items: ItemProps[];
  activeItem: ItemProps;
  toggleActiveItem: (item: ItemProps) => void;
  selectedDepatureFlight?: FlightProps;
  selectedReturnFlight?: FlightProps;
  selectedDepatureTransfer?: PackageTransferViewModel;
  selectedReturnTransfer?: PackageTransferViewModel;
  adultCount: number;
  childCount: number;
}) => {
  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);

  const { isDesktop } = useDevice();
  const [showNextArrow, setShowNextArrow] = useState(true);
  const [showPreviousArrow, setShowPreviousArrow] = useState(false);
  const swiperRef = useRef<SwiperCore>();

  useEffect(() => {
    // we should slide manually to the first slide since the initialSlide param is not working (swiper-8.1.0)
    swiperRef.current?.slideTo(0, 100);
  }, [query, items]);

  return (
    <Wrapper>
      {isDesktop && items?.length > 4 ? (
        <>
          {showPreviousArrow ? (
            <PreviousElement
              className='custom-prev-slide'
              onClick={() => swiperRef.current?.slidePrev()}
            >
              <CustomIcon
                width={16}
                height={26}
                icon={ChevronLeft}
                paletteColor='darkGrey'
              />
            </PreviousElement>
          ) : null}
          {showNextArrow ? (
            <NextElement
              className='custom-next-slide'
              onClick={() => swiperRef.current?.slideNext()}
            >
              <CustomIcon
                width={16}
                height={26}
                icon={ChevronRight}
                paletteColor='darkGrey'
              />
            </NextElement>
          ) : null}
        </>
      ) : null}
      <StyledSwiper
        slidesPerView={1.2}
        spaceBetween={10}
        slideToClickedSlide={!isDesktop}
        onBeforeInit={(swiper) => {
          swiperRef.current = swiper;
        }}
        onSlideChange={(swiper) => {
          setShowNextArrow(!swiper.isEnd); // show the next arrow icon as long as its not the end of the slider
          setShowPreviousArrow(!swiper.isBeginning); // show the previous arrow icon as long as its not the beginning of the slider
        }}
        breakpoints={{
          1240: {
            slidesPerView: 4,
            spaceBetween: 6,
          },
          840: {
            slidesPerView: 3,
            spaceBetween: 6,
          },
          640: {
            slidesPerView: 2,
            spaceBetween: 6,
          },
          450: {
            slidesPerView: 'auto',
            spaceBetween: 10,
          },
        }}
      >
        {items?.map((item) => (
          <SwiperSlide key={item.roomId} onClick={() => toggleActiveItem(item)}>
            <RoomsVariationBarItem
              {...item}
              isActive={activeItem?.roomId === item?.roomId ?? false}
              selectedDepatureFlight={selectedDepatureFlight}
              selectedReturnFlight={selectedReturnFlight}
              selectedDepatureTransfer={selectedDepatureTransfer}
              selectedReturnTransfer={selectedReturnTransfer}
              adultCount={adultCount}
              childCount={childCount}
            />
          </SwiperSlide>
        ))}
      </StyledSwiper>
    </Wrapper>
  );
};

export default RoomsVariationsBar;
